import React, { useCallback, useMemo, useRef, useState } from 'react'
import { makeStyles, CircularProgress } from '@material-ui/core'
import SydButton from '../../../commonDesign/Button'
import SydLinkButton from '../../../commonDesign/LinkButton'
import SydAutocomplete from '../../../commonDesign/SydAutocomplete'
import FadeIn from '../../../molecules/Transitions/FadeIn'
import EmptySection from '../../../atoms/EmptySection'
import PersonalSpace from '../shared/PersonalSpace'
import { usePolicy } from '../../../../hooks/usePolicy'
import { useGetClients } from '../../../../api/clients'
import useDebouncedValue from '../../../../hooks/useDebounceValue'
import { DEFAULT_SEARCH_CLIENTS_PAGE_SIZE } from '../../../../constants'
import {
  useCreateAltImportBatchMutation,
  useFinalizeAltImportBatchMutation
} from '../../../../api/altInvestments'

const ROLES = [
  { key: 'valuations', label: 'Valuations' },
  { key: 'ledger', label: 'Ledger' },
  { key: 'transactions', label: 'Transactions' }
]

const ACCEPTED_EXT = ['.xls', '.xlsx', '.xlsm', '.csv', '.tsv']
const MAX_FILE_BYTES = 25 * 1024 * 1024
const ACCEPT_ATTR = ACCEPTED_EXT.join(',')

const useStyles = makeStyles((theme) => ({
  container: {
    padding: '1rem',
    maxWidth: '720px'
  },
  intro: {
    marginBottom: '1rem',
    fontWeight: theme.typography.weights.light
  },
  clientPicker: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'column',
    gap: '6px'
  },
  clientPickerLabel: {
    fontWeight: 'bold'
  },
  slots: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    border: '1px solid var(--color-neutral-700)',
    borderRadius: 'var(--border-radius-md)',
    padding: '16px 20px'
  },
  slot: {
    display: 'grid',
    gridTemplateColumns: '140px 1fr 200px',
    gap: '12px',
    alignItems: 'center'
  },
  slotLabel: {
    fontWeight: 'bold'
  },
  pill: {
    padding: '4px 10px',
    borderRadius: '12px',
    fontSize: '0.85em',
    display: 'inline-block',
    maxWidth: '200px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  },
  pillIdle: { background: 'var(--color-neutral-200)', color: 'var(--color-neutral-700)' },
  pillUploading: { background: 'var(--color-info-100)', color: 'var(--color-info-700)' },
  pillDone: { background: 'var(--color-success-100)', color: 'var(--color-success-700)' },
  pillError: { background: 'var(--color-danger-100)', color: 'var(--color-danger-700)' },
  errors: {
    color: 'var(--color-danger-500)',
    marginTop: '8px'
  },
  banner: {
    padding: '10px 14px',
    borderRadius: 'var(--border-radius-md)',
    marginBottom: '12px'
  },
  bannerError: {
    background: 'var(--color-danger-100)',
    color: 'var(--color-danger-700)'
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '16px'
  },
  successPanel: {
    border: '1px solid var(--color-success-300)',
    borderRadius: 'var(--border-radius-md)',
    padding: '24px',
    background: 'var(--color-success-100)'
  },
  successHeader: {
    fontSize: '1.4em',
    fontWeight: 'bold',
    marginBottom: '12px',
    color: 'var(--color-success-700)'
  },
  successDetail: { marginBottom: '8px' },
  monospaceId: {
    fontFamily: 'monospace',
    background: 'var(--color-neutral-200)',
    padding: '2px 6px',
    borderRadius: '4px'
  },
  hero: {
    fontSize: theme.typography.fontSizes.h3,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '10px'
  }
}))

function validateFile (file) {
  if (!file) return null
  const lower = file.name.toLowerCase()
  const hasAcceptedExt = ACCEPTED_EXT.some(ext => lower.endsWith(ext))
  if (!hasAcceptedExt) {
    return `Unsupported file type. Accepted: ${ACCEPTED_EXT.join(', ')}`
  }
  if (file.size > MAX_FILE_BYTES) {
    return `File is too large (max ${MAX_FILE_BYTES / 1024 / 1024} MB)`
  }
  return null
}

function putFile ({ uploadUrl, requiredHeaders, file, onProgress }) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()
    xhr.open('PUT', uploadUrl, true)
    if (requiredHeaders) {
      Object.entries(requiredHeaders).forEach(([k, v]) => xhr.setRequestHeader(k, v))
    }
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable && onProgress) {
        onProgress(Math.round((e.loaded / e.total) * 100))
      }
    }
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve()
      else reject(new Error(`Upload failed with status ${xhr.status}`))
    }
    xhr.onerror = () => reject(new Error('Network error during upload'))
    xhr.send(file)
  })
}

function NewBatch () {
  const classes = useStyles()
  const inputRefs = {
    valuations: useRef(),
    ledger: useRef(),
    transactions: useRef()
  }
  const [filesByRole, setFilesByRole] = useState({ valuations: null, ledger: null, transactions: null })
  const [errorsByRole, setErrorsByRole] = useState({ valuations: null, ledger: null, transactions: null })
  const [progressByRole, setProgressByRole] = useState({ valuations: null, ledger: null, transactions: null })
  const [phase, setPhase] = useState('idle') // idle | uploading | finalizing | success | puts-failed | finalize-failed
  const [topError, setTopError] = useState(null)
  const [batchResult, setBatchResult] = useState(null)
  const [createdBatch, setCreatedBatch] = useState(null) // { batchId, files: [{ role, uploadUrl, requiredHeaders, ... }] }
  const [selectedClient, setSelectedClient] = useState(null)
  const [clientSearchValue, setClientSearchValue] = useState('')
  const debouncedClientSearch = useDebouncedValue(clientSearchValue, 500)
  const { data: clientOptions, isLoading: clientsLoading } = useGetClients({
    searchValue: debouncedClientSearch,
    take: DEFAULT_SEARCH_CLIENTS_PAGE_SIZE
  })

  const { createImportBatch } = useCreateAltImportBatchMutation()
  const { finalizeImportBatch } = useFinalizeAltImportBatchMutation()

  const populatedRoles = useMemo(() => Object.keys(filesByRole).filter(r => filesByRole[r]), [filesByRole])
  const canSubmit = phase === 'idle' && selectedClient != null && populatedRoles.length > 0 && Object.values(errorsByRole).every(e => !e)

  const autocompleteOptions = useMemo(() => (
    (clientOptions ?? []).map(({ clientId, shortName }) => ({ value: clientId, label: shortName }))
  ), [clientOptions])

  const onClientChange = useCallback((_e, option) => {
    if (!option) {
      setSelectedClient(null)
      return
    }
    const full = (clientOptions ?? []).find(c => c.clientId === option.value)
    setSelectedClient(full ? { clientId: full.clientId, shortName: full.shortName } : null)
  }, [clientOptions])

  const onFileChange = useCallback((role) => () => {
    const file = inputRefs[role].current.files?.[0] ?? null
    const err = validateFile(file)
    setErrorsByRole(prev => ({ ...prev, [role]: err }))
    setFilesByRole(prev => ({ ...prev, [role]: err ? null : file }))
    setProgressByRole(prev => ({ ...prev, [role]: null }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const reset = useCallback(() => {
    Object.values(inputRefs).forEach(ref => { if (ref.current) ref.current.value = null })
    setFilesByRole({ valuations: null, ledger: null, transactions: null })
    setErrorsByRole({ valuations: null, ledger: null, transactions: null })
    setProgressByRole({ valuations: null, ledger: null, transactions: null })
    setPhase('idle')
    setTopError(null)
    setBatchResult(null)
    setCreatedBatch(null)
    setSelectedClient(null)
    setClientSearchValue('')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const uploadAll = useCallback(async (batch) => {
    const results = await Promise.allSettled(batch.files.map(async (entry) => {
      setProgressByRole(prev => ({ ...prev, [entry.role]: { state: 'uploading', percent: 0 } }))
      try {
        await putFile({
          uploadUrl: entry.uploadUrl,
          requiredHeaders: entry.requiredHeaders,
          file: filesByRole[entry.role],
          onProgress: (percent) => {
            setProgressByRole(prev => ({ ...prev, [entry.role]: { state: 'uploading', percent } }))
          }
        })
        setProgressByRole(prev => ({ ...prev, [entry.role]: { state: 'done' } }))
      } catch (err) {
        setProgressByRole(prev => ({ ...prev, [entry.role]: { state: 'error', message: err.message } }))
        throw err
      }
    }))
    return results.every(r => r.status === 'fulfilled')
  }, [filesByRole])

  const submit = useCallback(async () => {
    setTopError(null)
    setPhase('uploading')

    const filesPayload = populatedRoles.map(role => ({
      role,
      filename: filesByRole[role].name,
      size: filesByRole[role].size
    }))

    let batch
    try {
      batch = await createImportBatch({ clientId: selectedClient.clientId, files: filesPayload })
      setCreatedBatch(batch)
    } catch (err) {
      console.error(err)
      setTopError('Could not create the import batch on the server.')
      setPhase('idle')
      return
    }

    const allOk = await uploadAll(batch)
    if (!allOk) {
      setPhase('puts-failed')
      return
    }

    setPhase('finalizing')
    try {
      const result = await finalizeImportBatch({ batchId: batch.batchId })
      setBatchResult({ batchId: batch.batchId, files: filesPayload, ...result })
      setPhase('success')
    } catch (err) {
      console.error(err)
      setPhase('finalize-failed')
    }
  }, [populatedRoles, filesByRole, createImportBatch, finalizeImportBatch, uploadAll, selectedClient])

  const retryFailedPuts = useCallback(async () => {
    if (!createdBatch) return
    setPhase('uploading')
    const failingRoles = Object.entries(progressByRole)
      .filter(([, p]) => p?.state === 'error')
      .map(([r]) => r)
    const subset = {
      ...createdBatch,
      files: createdBatch.files.filter(f => failingRoles.includes(f.role))
    }
    const allOk = await uploadAll(subset)
    if (!allOk) {
      setPhase('puts-failed')
      return
    }
    setPhase('finalizing')
    try {
      const result = await finalizeImportBatch({ batchId: createdBatch.batchId })
      setBatchResult({ batchId: createdBatch.batchId, files: populatedRoles.map(r => ({ role: r, filename: filesByRole[r].name })), ...result })
      setPhase('success')
    } catch (err) {
      console.error(err)
      setPhase('finalize-failed')
    }
  }, [createdBatch, progressByRole, uploadAll, finalizeImportBatch, populatedRoles, filesByRole])

  const retryFinalize = useCallback(async () => {
    if (!createdBatch) return
    setPhase('finalizing')
    try {
      const result = await finalizeImportBatch({ batchId: createdBatch.batchId })
      setBatchResult({ batchId: createdBatch.batchId, files: populatedRoles.map(r => ({ role: r, filename: filesByRole[r].name })), ...result })
      setPhase('success')
    } catch (err) {
      console.error(err)
      setPhase('finalize-failed')
    }
  }, [createdBatch, finalizeImportBatch, populatedRoles, filesByRole])

  const canAccess = usePolicy('admin_alt_investments')

  if (!canAccess) {
    return (
      <FadeIn className={classes.container}>
        <EmptySection
          title='Access Denied'
          description='You do not have sufficient permissions to access this resource'
        />
      </FadeIn>
    )
  }

  if (phase === 'success') {
    const uploadedKeys =
      (createdBatch?.files ?? []).map(e => e.key).filter(Boolean)
    const filenames = batchResult?.files?.map(f => f.filename).filter(Boolean)

    return (
      <FadeIn className={classes.container}>
        <div className={classes.successPanel}>
          <div className={classes.successHeader}>✓ Batch uploaded successfully</div>
          <div className={classes.successDetail}>
            Batch ID: <span className={classes.monospaceId}>{batchResult?.batchId}</span>
          </div>
          {selectedClient ? (
            <div className={classes.successDetail}>
              Client: <b>{selectedClient.shortName}</b>{' '}
              (<span className={classes.monospaceId}>{selectedClient.clientId}</span>)
            </div>
          ) : null}
          <div className={classes.successDetail}>
            Uploaded file names:{' '}
            {(filenames && filenames.length > 0) ? filenames.join(', ') : '-'}
          </div>
          {uploadedKeys.length > 0 ? (
            <div className={classes.successDetail}>
              S3 keys:{' '}
              <span className={classes.monospaceId}>{uploadedKeys.join(', ')}</span>
            </div>
          ) : null}
          <div className={classes.successDetail}>
            The Workflow service has been notified.
          </div>
          <div className={classes.actions}>
            <SydButton size='md' variant='primary' onClick={reset} icon='add'>
              Upload another batch
            </SydButton>
            <SydLinkButton size='md' variant='outline' to='/admin/accounts' icon='back'>
              Back to Accounts
            </SydLinkButton>
          </div>
        </div>
        <PersonalSpace />
      </FadeIn>
    )
  }

  return (
    <FadeIn className={classes.container}>
      <div className={classes.intro}>
        <p>
          Upload alternative-investment files for processing. Each slot is mapped to a known role;
          assign each file to its slot. Only the slots you populate are uploaded.
        </p>
        <ul>
          <li>Accepted file types: <b>{ACCEPTED_EXT.join(', ')}</b></li>
          <li>Max file size: <b>25&nbsp;MB</b></li>
        </ul>
      </div>

      {topError ? (
        <div className={`${classes.banner} ${classes.bannerError}`}>{topError}</div>
      ) : null}

      {phase === 'puts-failed' ? (
        <div className={`${classes.banner} ${classes.bannerError}`}>
          Some files failed to upload. Retry the failed ones — successful uploads will not be re-sent.
        </div>
      ) : null}

      {phase === 'finalize-failed' ? (
        <div className={`${classes.banner} ${classes.bannerError}`}>
          Files uploaded, but workflow notification failed.
        </div>
      ) : null}

      <div className={classes.clientPicker}>
        <label className={classes.clientPickerLabel} htmlFor='alt-import-client'>
          Client <span style={{ color: 'var(--color-danger-500)' }}>*</span>
        </label>
        <SydAutocomplete
          name='alt-import-client'
          options={autocompleteOptions}
          isLoading={clientsLoading}
          disabled={phase === 'uploading' || phase === 'finalizing'}
          placeholder='Search for a client…'
          onInputChange={(_e, v) => setClientSearchValue(v ?? '')}
          onChange={onClientChange}
          value={selectedClient ? { value: selectedClient.clientId, label: selectedClient.shortName } : null}
          size='sm'
        />
      </div>

      <div className={classes.slots}>
        {ROLES.map(({ key, label }) => {
          const file = filesByRole[key]
          const err = errorsByRole[key]
          const progress = progressByRole[key]
          const disabled = phase === 'uploading' || phase === 'finalizing'
          const pill = progress?.state === 'uploading'
            ? <span className={`${classes.pill} ${classes.pillUploading}`}>uploading {progress.percent}%</span>
            : progress?.state === 'done'
              ? <span className={`${classes.pill} ${classes.pillDone}`}>✓ done</span>
              : progress?.state === 'error'
                ? <span className={`${classes.pill} ${classes.pillError}`}>error — {progress.message}</span>
                : file
                  ? <span className={`${classes.pill} ${classes.pillIdle}`} title={file.name}>{file.name}</span>
                  : <span className={`${classes.pill} ${classes.pillIdle}`}>no file</span>
          return (
            <div key={key} className={classes.slot}>
              <div className={classes.slotLabel}>{label}</div>
              <div>
                <input
                  type='file'
                  ref={inputRefs[key]}
                  onChange={onFileChange(key)}
                  accept={ACCEPT_ATTR}
                  disabled={disabled}
                />
                {err ? <div className={classes.errors}>{err}</div> : null}
              </div>
              <div>{pill}</div>
            </div>
          )
        })}
      </div>

      <div className={classes.actions}>
        {phase === 'puts-failed' ? (
          <SydButton size='md' variant='primary' icon='check' onClick={retryFailedPuts}>
            Retry failed uploads
          </SydButton>
        ) : phase === 'finalize-failed' ? (
          <>
            <SydButton size='md' variant='primary' icon='check' onClick={retryFinalize}>
              Retry finalize
            </SydButton>
            <SydButton size='md' variant='outline' icon='close' onClick={reset}>
              Discard and start over
            </SydButton>
          </>
        ) : (
          <>
            <SydButton size='md' variant='outline' icon='close' onClick={reset} disabled={phase !== 'idle'}>
              Reset
            </SydButton>
            <SydButton
              size='md'
              variant='primary'
              icon='check'
              disabled={!canSubmit}
              onClick={submit}
            >
              {phase === 'uploading' ? 'Uploading…' : phase === 'finalizing' ? 'Notifying workflow service…' : 'Upload'}
            </SydButton>
            {phase === 'uploading' || phase === 'finalizing' ? (
              <div className={classes.hero}><CircularProgress size={20} /></div>
            ) : null}
          </>
        )}
      </div>

      <PersonalSpace />
    </FadeIn>
  )
}

export default NewBatch
